import React, { useEffect, useRef, useState } from "react";
import {
  TimespanItem,
  TimespanSelect,
  SelectTimespanBox,
  StyledSelectTimespan,
  StyledCounterItem,
} from "./SelectTimespan.styled";
import Input from "../core/Input/Input";
import { IconManager } from "../core/IconManager/IconManager";
import { FontWeightEnum, IconSizeEnum } from "../../types/AppTypes";
import { theme } from "../../constants/StyledComponents";
import { ReactComponent as ChevronDownIcon } from "../../icons/chevronDown.svg";
import { ReactComponent as ChevronUpIcon } from "../../icons/chevronUp.svg";
import { BodyTyp } from "../Typography/Typography";
import {
  timespanLabels,
  TimestampLabelEnum,
} from "../../constants/AppConstants";

const isCounterMin = (x: number) => {
  return x <= 1;
};

const isCounterMax = (x: number) => {
  return x >= 999;
};

interface CounterProps {
  value: number;
  onChange: (value: number) => void;
}

const CounterItem = ({ value, onChange }: CounterProps) => {
  return (
    <StyledCounterItem>
      <IconManager
        size={IconSizeEnum.MEDIUM}
        onClick={isCounterMax(value) ? undefined : () => onChange(value + 1)}
        color={isCounterMax(value) ? theme.colors.grey_4 : theme.colors.black}
      >
        <ChevronUpIcon />
      </IconManager>
      <BodyTyp variant={"1"} fontWeight={FontWeightEnum.SEMIBOLD}>
        {value}
      </BodyTyp>
      <IconManager
        size={IconSizeEnum.MEDIUM}
        onClick={isCounterMin(value) ? undefined : () => onChange(value - 1)}
        color={isCounterMin(value) ? theme.colors.grey_4 : theme.colors.black}
      >
        <ChevronDownIcon />
      </IconManager>
    </StyledCounterItem>
  );
};

export interface Timespan {
  counter: number;
  timeSpan: string;
}

interface SelectTimespanProps {
  label: string;
  timestampLabels: TimestampLabelEnum[];
  onChange: (timespan: string | null) => void;
  value?: string;
}

const parseTimespanString = (timespanString?: string): Timespan | undefined => {
  if (!timespanString) return undefined;

  const [numberStr, label] = timespanString.split(" ");
  const number = parseInt(numberStr, 10);

  const timeSpanEntry = Object.entries(timespanLabels).find(
    ([, value]) => value.singular === label || value.plural === label,
  );

  if (!timeSpanEntry) return undefined;

  const [, value] = timeSpanEntry;
  return {
    counter: number,
    timeSpan: number === 1 ? value.singular : value.plural,
  };
};

const findTimespanKeyByValue = (
  timespanValue: string,
): TimestampLabelEnum | undefined => {
  const entry = Object.entries(timespanLabels).find(
    ([, value]) =>
      value.singular === timespanValue || value.plural === timespanValue,
  );
  return entry ? (entry[0] as TimestampLabelEnum) : undefined;
};

export const SelectTimespan = ({
  label,
  onChange,
  value,
  timestampLabels,
  ...props
}: SelectTimespanProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const parsedTimespan = parseTimespanString(value);
  const [timespan, setTimespan] = useState<Timespan | undefined>(
    parsedTimespan,
  );
  const [counterNumber, setCounterNumber] = useState<number>(
    parsedTimespan?.counter || 1,
  );

  useEffect(() => {
    const parsedTimespan = parseTimespanString(value);
    setTimespan(parsedTimespan);
    setCounterNumber(parsedTimespan?.counter || 1);
  }, [value]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const clear = () => {
    setIsOpen(false);
    setTimespan(undefined);
    onChange(null);
  };

  const useOutsideAlerter = (ref: React.RefObject<HTMLDivElement>) => {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(wrapperRef);

  const handleCounterChange = (newCounter: number) => {
    setCounterNumber(newCounter);
    if (timespan) {
      const timespanKey = findTimespanKeyByValue(timespan.timeSpan);
      if (timespanKey) {
        const newTimespanLabel = isCounterMin(newCounter)
          ? timespanLabels[timespanKey].singular
          : timespanLabels[timespanKey].plural;

        const newTimespan = {
          counter: newCounter,
          timeSpan: newTimespanLabel,
        };
        setTimespan(newTimespan);
        onChange(`${newCounter} ${newTimespanLabel}`);
      }
    }
  };

  const selectTimespan = (selectedLabel: {
    singular: string;
    plural: string;
  }) => {
    const newTimespanValue =
      counterNumber === 1 ? selectedLabel.singular : selectedLabel.plural;

    const newTimespan = {
      counter: counterNumber,
      timeSpan: newTimespanValue,
    };

    setTimespan(newTimespan);
    onChange(`${counterNumber} ${newTimespanValue}`);
    setIsOpen(false);
  };

  const isTimespanSelected = (value: { singular: string; plural: string }) => {
    if (!timespan) return false;

    const currentKey = findTimespanKeyByValue(timespan.timeSpan);
    if (!currentKey) return false;

    const checkKey = Object.entries(timespanLabels).find(
      ([, labelValue]) => labelValue === value,
    )?.[0] as TimestampLabelEnum;

    return currentKey === checkKey;
  };

  return (
    <StyledSelectTimespan>
      <Input
        onClick={toggleOpen}
        endIcon={
          <IconManager
            size={IconSizeEnum.SMALL}
            onClick={toggleOpen}
            color={theme.colors.black}
          >
            <ChevronDownIcon />
          </IconManager>
        }
        label={label}
        readOnly
        allowClear
        value={
          timespan ? `${timespan.counter} ${timespan.timeSpan}` : undefined
        }
        onClear={clear}
        {...props}
      />
      {isOpen && (
        <SelectTimespanBox ref={wrapperRef}>
          <CounterItem value={counterNumber} onChange={handleCounterChange} />
          <TimespanSelect>
            {Object.entries(timespanLabels)
              .filter(([key]) =>
                timestampLabels.includes(key as TimestampLabelEnum),
              )
              .map(([key, value]) => (
                <TimespanItem
                  key={key}
                  $selected={isTimespanSelected(value)}
                  onClick={() => selectTimespan(value)}
                >
                  <BodyTyp
                    variant={"2"}
                    fontWeight={FontWeightEnum.MEDIUM}
                    color={
                      isTimespanSelected(value)
                        ? theme.colors.white
                        : theme.colors.black
                    }
                  >
                    {isCounterMin(counterNumber)
                      ? value.singular
                      : value.plural}
                  </BodyTyp>
                </TimespanItem>
              ))}
          </TimespanSelect>
        </SelectTimespanBox>
      )}
    </StyledSelectTimespan>
  );
};
